import React from 'react';
import type { EditingMessage } from '../components/ChatInput';
import type { ChatDoc, SendResult } from '../types/chat';
import type { UseChatSurfaceResult } from './useChatSurface';

/** The slice of `useChatSurface` the submit handler drives. */
export type ChatSubmitSurface = Pick<
    UseChatSurfaceResult,
    | 'sending'
    | 'setSending'
    | 'pendingFiles'
    | 'setPendingFiles'
    | 'beginPendingMessage'
    | 'markPendingMessageFailed'
    | 'setFailedFileNames'
    | 'clearFailedFileNames'
>;

export interface UseChatSubmitOptions {
    /** The object returned by `useChatSurface` (or the subset it needs). */
    surface: ChatSubmitSurface;
    send: (text: string, files: File[], doc?: ChatDoc) => Promise<SendResult>;
    edit: (
        messageId: string,
        newText: string,
        files?: File[],
        doc?: ChatDoc
    ) => Promise<SendResult>;
    /** Editing state stays in the surface — it also drives the input banner. */
    editingMessage: EditingMessage | null;
    setEditingMessage: (message: EditingMessage | null) => void;
    /**
     * Whether staged attachments are sent with an edit. Off for surfaces whose
     * edit endpoint takes no files.
     */
    attachmentsOnEdit?: boolean;
    /** Surface-supplied error feedback (e.g. a toast) when saving an edit fails. */
    onEditError?: (message: string) => void;
}

const EDIT_FAILED_MESSAGE = 'Failed to edit message';

/**
 * The one submit handler every chat surface uses: optimistic send with the
 * pending bubble, or an in-place edit when the surface is in edit mode.
 */
export const useChatSubmit = ({
    surface,
    send,
    edit,
    editingMessage,
    setEditingMessage,
    attachmentsOnEdit = false,
    onEditError
}: UseChatSubmitOptions): ((html: string, doc?: ChatDoc) => Promise<void>) => {
    const {
        sending,
        setSending,
        pendingFiles,
        setPendingFiles,
        beginPendingMessage,
        markPendingMessageFailed,
        setFailedFileNames,
        clearFailedFileNames
    } = surface;

    return React.useCallback(
        async (html: string, doc?: ChatDoc): Promise<void> => {
            if (sending) return;

            // Edit mode: save the edit and exit, no pending bubble / no AI reply trigger.
            if (editingMessage) {
                const trimmed = html.trim();
                if (!trimmed) return;
                const messageFiles = attachmentsOnEdit ? [...pendingFiles] : [];
                try {
                    setSending(true);
                    if (attachmentsOnEdit) {
                        clearFailedFileNames();
                        const result = await edit(editingMessage.id, trimmed, messageFiles, doc);
                        if (result.failedFiles.length > 0) setFailedFileNames(result.failedFiles);
                        setPendingFiles([]);
                    } else {
                        await edit(editingMessage.id, trimmed, undefined, doc);
                    }
                    setEditingMessage(null);
                } catch {
                    // Keep edit mode open so the user can retry or cancel.
                    onEditError?.(EDIT_FAILED_MESSAGE);
                } finally {
                    setSending(false);
                }
                return;
            }

            const messageFiles = [...pendingFiles];
            try {
                setSending(true);
                clearFailedFileNames();
                beginPendingMessage(html, messageFiles, doc);
                setPendingFiles([]);
                const result = await send(html, messageFiles, doc);
                if (result.failedFiles.length > 0) setFailedFileNames(result.failedFiles);
            } catch {
                markPendingMessageFailed();
            } finally {
                setSending(false);
            }
        },
        [
            sending,
            setSending,
            pendingFiles,
            setPendingFiles,
            beginPendingMessage,
            markPendingMessageFailed,
            setFailedFileNames,
            clearFailedFileNames,
            send,
            edit,
            editingMessage,
            setEditingMessage,
            attachmentsOnEdit,
            onEditError
        ]
    );
};
